import { lazy } from 'react';

import type {
  IntegrationDefinition,
  IntegrationLoaderContext,
  NormalizedConnection,
} from '@mint-lib/integration-registry';
import {
  fetchOmniConnections,
  OMNI_URN,
  omniConnectionRepository,
} from '@mint-lib/omni-integration';
import type { QueryClient } from '@mint-lib/query-client';

import { amazonOAuthFlow } from './oauth.js';

async function prefetch(
  ctx: IntegrationLoaderContext,
): Promise<{ connections?: NormalizedConnection[] }> {
  const queryClient = ctx.queryClient as QueryClient;
  return {
    connections: await fetchOmniConnections(queryClient, OMNI_URN.amazon),
  };
}

/**
 * Amazon's own `IntegrationDefinition` — a one-way `dsp` integration built on
 * `@mint-lib/omni-integration`'s shared repository (list/verify/delete are
 * identical to the generic omni platforms), with its own region-aware OAuth
 * flow and its own add/manage forms.
 */
export function createAmazonIntegration(): IntegrationDefinition {
  return {
    meta: {
      urn: OMNI_URN.amazon,
      displayName: 'Amazon',
      i18nNamespace: '@mint-lib/amazon-integration',
      channel: 'dsp',
      direction: 'one-way',
      showAdAccounts: true,
    },
    connection: {
      // Never imported eagerly — always via `React.lazy`, so `src/index.ts`
      // (which re-exports this factory) stays free of the form chunks.
      AddForm: lazy(() => import('@mint-lib/amazon-integration/add-form')),
      ManageForm: lazy(
        () => import('@mint-lib/amazon-integration/manage-form'),
      ),
      repository: omniConnectionRepository,
      oauth: amazonOAuthFlow,
      prefetch,
    },
  };
}
